import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Bell, AlertTriangle, Clock, CheckCircle2, CheckCheck } from "lucide-react";

const TYPE_ICON = {
  overdue: { icon: AlertTriangle, cls: "text-rose-600 bg-rose-50" },
  due_soon: { icon: Clock, cls: "text-amber-600 bg-amber-50" },
  task: { icon: CheckCircle2, cls: "text-blue-600 bg-blue-50" },
};

function timeAgo(iso) {
  try {
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return "ora";
    if (diff < 3600) return `${Math.floor(diff / 60)} min fa`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} h fa`;
    return `${Math.floor(diff / 86400)} g fa`;
  } catch { return ""; }
}

export default function NotificationsBell() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const ref = useRef(null);

  const load = useCallback(() => {
    api.get("/notifications")
      .then(({ data }) => { setItems(data.notifications || []); setUnread(data.unread || 0); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const openItem = async (n) => {
    setOpen(false);
    if (!n.read) {
      await api.post(`/notifications/${n.id}/read`).catch(() => {});
      load();
    }
    if (n.link) navigate(n.link);
  };

  const markAll = async () => {
    await api.post("/notifications/read-all").catch(() => {});
    load();
  };

  return (
    <div className="relative" ref={ref}>
      <button
        className="relative p-2 text-slate-500 hover:text-slate-800"
        data-testid="notifications-btn"
        onClick={() => setOpen((o) => !o)}
      >
        <Bell className="h-5 w-5" />
        {unread > 0 && (
          <span
            className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-[#E11D48] px-1 text-[10px] font-bold text-white"
            data-testid="notifications-badge"
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="fixed inset-x-2 top-16 z-50 max-h-[70vh] overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-xl sm:absolute sm:inset-x-auto sm:right-0 sm:top-11 sm:w-80" data-testid="notifications-panel">
          <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
            <span className="font-display text-sm font-semibold text-slate-800">Notifiche</span>
            {items.length > 0 && (
              <button onClick={markAll} className="flex items-center gap-1 text-xs text-slate-500 hover:text-[#E11D48]" data-testid="notifications-read-all">
                <CheckCheck className="h-3.5 w-3.5" /> Segna lette
              </button>
            )}
          </div>
          {items.length === 0 ? (
            <div className="px-4 py-10 text-center text-sm text-slate-400" data-testid="notifications-empty">
              Nessuna notifica
            </div>
          ) : (
            <div className="divide-y divide-slate-50">
              {items.map((n) => {
                const meta = TYPE_ICON[n.type] || TYPE_ICON.task;
                const Icon = meta.icon;
                return (
                  <button
                    key={n.id}
                    onClick={() => openItem(n)}
                    className={`flex w-full items-start gap-3 px-4 py-3 text-left hover:bg-slate-50 ${n.read ? "" : "bg-rose-50/40"}`}
                    data-testid="notification-item"
                  >
                    <span className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${meta.cls}`}>
                      <Icon className="h-4 w-4" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center gap-2">
                        <span className="truncate text-sm font-medium text-slate-800">{n.title}</span>
                        {!n.read && <span className="h-2 w-2 shrink-0 rounded-full bg-[#E11D48]" />}
                      </span>
                      <span className="block truncate text-xs text-slate-500">{n.message}</span>
                      <span className="mt-0.5 block text-[11px] text-slate-400">{timeAgo(n.created_at)}</span>
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
